import { createContext, useContext, useEffect, useLayoutEffect, useMemo, type ReactNode } from "react";
import { Outlet, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Theme } from "@astryxdesign/core/theme";
import { VStack } from "@astryxdesign/core/VStack";
import { Center } from "@astryxdesign/core/Center";
import { Spinner } from "@astryxdesign/core/Spinner";
import { publicApi } from "@/lib/api/public";
import type { Studio } from "@/lib/api/schemas";
import { studioTheme, LOADING_ACCENT, type HeadingFont } from "@/theme/studio-theme";
import { registerStudioWorker } from "@/lib/pwa";
import { ErrorState } from "@/components/states";
import { envError } from "@/lib/env";
import { Banner } from "@astryxdesign/core/Banner";
import { applyStudioLanguages, LANGS, useT, type Lang } from "@/lib/i18n";

const StudioContext = createContext<Studio | null>(null);

/** Interface languages this studio offers (both when the config predates the setting). */
export function useStudioLanguages(): readonly Lang[] {
  return useContext(StudioContext)?.config.languages ?? LANGS;
}

export function useStudio(): Studio {
  const s = useContext(StudioContext);
  if (!s) throw new Error("useStudio outside StudioRoot");
  return s;
}

export const studioQueryKey = (slug: string) => ["studio", slug] as const;

/** Accent written into the static shell by build-shells: correct colour on first paint. */
function shellAccent(): string {
  const v = document.querySelector<HTMLMetaElement>('meta[name="studio-accent"]')?.content;
  return v && /^#[0-9A-F]{6}$/i.test(v) ? v : LOADING_ACCENT;
}

export function StudioThemed({
  accent,
  headingFont,
  accentText,
  detail,
  children,
}: {
  accent: string;
  headingFont?: HeadingFont;
  accentText?: string;
  detail?: string;
  children: ReactNode;
}) {
  const theme = useMemo(() => studioTheme(accent, headingFont, accentText), [accent, headingFont, accentText]);
  // Decorative brand colour (rings, glass tint, frames) read by app.css as var(--studio-detail).
  useLayoutEffect(() => {
    const root = document.documentElement.style;
    if (detail) root.setProperty("--studio-detail", detail);
    else root.removeProperty("--studio-detail");
  }, [detail]);
  return (
    <Theme theme={theme} mode="light">
      {children}
    </Theme>
  );
}

export function StudioRoot() {
  const t = useT();
  const { slug = "" } = useParams();
  const query = useQuery({
    queryKey: studioQueryKey(slug),
    queryFn: () => publicApi.studio(slug),
    staleTime: 5 * 60_000,
    enabled: !envError,
  });

  useEffect(() => {
    void registerStudioWorker(slug);
  }, [slug]);

  const accent = query.data?.config.accent ?? shellAccent();
  const languages = query.data?.config.languages;
  useLayoutEffect(() => {
    if (query.data) applyStudioLanguages(languages ?? LANGS);
  }, [query.data, languages]);

  if (envError) {
    return (
      <StudioThemed accent={accent}>
        <VStack padding={4}>
          <Banner status="error" title={t.studio.notConfigured} description={t.studio.missingEnv(envError)} collapsible={false} />
        </VStack>
      </StudioThemed>
    );
  }

  return (
    <StudioThemed accent={accent} headingFont={query.data?.config.heading_font} accentText={query.data?.config.accent_text} detail={query.data?.config.detail}>
      {query.isPending ? (
        <Center height="100dvh">
          <Spinner size="xl" label={t.studio.loading} />
        </Center>
      ) : query.isError ? (
        <VStack padding={4} className="app-safe-top">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} title={t.studio.unavailable} />
        </VStack>
      ) : (
        <StudioContext.Provider value={query.data}>
          <Outlet />
        </StudioContext.Provider>
      )}
    </StudioThemed>
  );
}
